import React from 'react';

const EstimateDisplay = ({ costEstimate, className = "", questionNumber = 1 }) => {
  if (!costEstimate || !costEstimate.min || !costEstimate.max) {
    return (
      <div className={`text-center ${className}`}>
        <div className="text-2xl font-bold text-gray-900 mb-2">$1.60 Mn - $3.00 Mn</div>
        <div className="text-sm text-gray-600 mb-3">Total Cost of Construction</div>
        
        {/* Estimate Accuracy Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
            <span>Estimate Accuracy</span>
            <span>{Math.min(questionNumber * 10, 100)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div 
              className="bg-gradient-to-r from-gray-400 to-green-500 h-1 rounded-full transition-all duration-700"
              style={{ width: `${Math.min(questionNumber * 10, 100)}%` }}
            ></div>
          </div>
        </div>
        
        {/* Cost per Sq Ft and Square Footage - Side by Side */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">$640 - $1200</div>
            <div className="text-xs text-gray-600">Cost per Sq ft</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">2,500</div>
            <div className="text-xs text-gray-600">Sq ft</div>
          </div>
        </div>
      </div>
    );
  }

  const { min, max, sq_ft, features } = costEstimate;
  
  // Format cost in millions
  const formatCost = (cost) => {
    if (cost >= 1000000) {
      return `$${(cost / 1000000).toFixed(2)} Mn`;
    } else if (cost >= 1000) {
      return `$${(cost / 1000).toFixed(0)}K`;
    } else {
      return `$${cost.toLocaleString()}`;
    }
  };

  // Calculate per sq ft costs
  const minPerSqFt = sq_ft ? Math.round(min / sq_ft) : 0;
  const maxPerSqFt = sq_ft ? Math.round(max / sq_ft) : 0;

  // Format features for display
  const formatFeatures = (features) => {
    if (!features || features.length === 0) {
      return [];
    }
    
    const formattedFeatures = [];
    features.forEach(featureObj => {
      Object.entries(featureObj).forEach(([key, value]) => {
        // Only add features that have a meaningful cost (> 0)
        if (value > 0) {
          const featureName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          formattedFeatures.push({
            name: featureName,
            cost: value
          });
        }
      });
    });
    
    return formattedFeatures;
  };

  const displayFeatures = formatFeatures(features);

  return (
    <div className={`${className}`}>
      {/* Total Cost Range */}
      <div className="text-center mb-6">
        <div className="text-2xl font-bold text-gray-900 mb-2">
          {formatCost(min)} - {formatCost(max)}
        </div>
        <div className="text-sm text-gray-600 mb-3">Total Cost of Construction</div>
        
        {/* Estimate Accuracy Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
            <span>Estimate Accuracy</span>
            <span>{Math.min(questionNumber * 10, 100)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div 
              className="bg-gradient-to-r from-gray-400 to-green-500 h-1 rounded-full transition-all duration-700"
              style={{ width: `${Math.min(questionNumber * 10, 100)}%` }}
            ></div>
          </div>
        </div>
        
        {/* Cost per Sq Ft and Square Footage - Side by Side */}
        <div className="grid grid-cols-2 gap-4">
          {/* Cost per Sq Ft */}
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              ${minPerSqFt} - ${maxPerSqFt}
            </div>
            <div className="text-xs text-gray-600">Cost per Sq ft</div>
          </div>
          
          {/* Square Footage */}
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {sq_ft ? sq_ft.toLocaleString() : '2,500'}
            </div>
            <div className="text-xs text-gray-600">Sq ft</div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      {displayFeatures.length > 0 && (
        <div>
          <div className="text-sm font-medium text-gray-700 mb-3">
            Additions from Base Model
          </div>
          <div className="space-y-3">
            {displayFeatures.map((feature, index) => (
              <div key={index} className="flex items-center py-1">
                <span className="text-sm font-bold text-gray-800">{feature.name}</span>
                <div className="flex-1 mx-3 border-b border-dotted border-gray-400"></div>
                <span className="text-sm font-semibold text-gray-900">
                  +${feature.cost >= 1000 ? `${(feature.cost / 1000).toFixed(0)}K` : feature.cost.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimateDisplay;
